// rraCaseList — the Accommodations queue's "needs action" view.
//
// The queue lists EVERY case, closed ones included, newest first. A closed case can still owe staff
// work: the decision tasks RRA v2.2 part B raises in AppFolio (start charges after a denial, keep them
// off or resume them on a tenancy) outlive the case, and a pending one raises needs-attention. So the
// queue marks such a case on its row ("AppFolio task pending"), and a "Needs action" view lists the
// cases that need attention OR hold a pending AppFolio task — open or closed — so none is buried under
// a long history of finished cases.
//
//   cd apps/web && npx tsx --test src/lib/rraCaseList.test.ts
//
// Type-only imports of the client; names and counts only.

import type { RraCaseView, RraFreezeActionView } from './rraStaffClient';

export type QueueView = 'all' | 'action';

export const QUEUE_VIEW_LABEL: Record<QueueView, string> = {
  all: 'All cases',
  action: 'Needs action',
};

/** The row pill on a case that holds an AppFolio task staff have not marked applied. */
export const APPFOLIO_TASK_PENDING = 'AppFolio task pending';
export const NOTHING_NEEDS_ACTION = 'Nothing needs action right now.';

/** The AppFolio tasks still waiting for staff — on any case, open or closed. */
export function pendingAppfolioTasks(c: Pick<RraCaseView, 'freeze'>): RraFreezeActionView[] {
  return (c.freeze?.appfolioActions ?? []).filter((a) => a.status === 'pending_staff');
}

export function hasPendingAppfolioTask(c: Pick<RraCaseView, 'freeze'>): boolean {
  return pendingAppfolioTasks(c).length > 0;
}

/** A case staff must act on: the API flagged it, or an AppFolio task is waiting (a closed case included). */
export function needsAction(c: Pick<RraCaseView, 'needsAttention' | 'freeze'>): boolean {
  return c.needsAttention === true || hasPendingAppfolioTask(c);
}

/** The cases a view shows, in the order given. */
export function visibleCases<T extends Pick<RraCaseView, 'needsAttention' | 'freeze'>>(
  cases: ReadonlyArray<T>,
  view: QueueView,
): T[] {
  return view === 'action' ? cases.filter(needsAction) : [...cases];
}

export function actionCount(cases: ReadonlyArray<Pick<RraCaseView, 'needsAttention' | 'freeze'>>): number {
  return cases.filter(needsAction).length;
}
